import { initSizesScroll, initSizeSelect } from '../productCard.ts';

// ─── BESTSELLERS CAROUSEL ───
// Les cartes (.product-card) sont rendues en SSR à partir des vrais produits
// (voir src/server/views/pages/home.ts) — ce module ne fait que piloter la
// navigation du carrousel sur les cartes déjà présentes dans le DOM.

function initColorSwatches(): void {
  document.querySelectorAll<HTMLElement>('.product-card').forEach((card) => {
    const swatches = card.querySelectorAll<HTMLElement>('.product-card__color');
    const images = card.querySelectorAll<HTMLImageElement>('.product-card__image-wrap img');
    if (!swatches.length || !images.length) return;

    let selectedIdx = 0;

    function showImg(index: number): void {
      images.forEach((img, i) => img.classList.toggle('is-active', i === index));
    }

    function selectSwatch(index: number): void {
      swatches.forEach((s, i) => s.classList.toggle('is-active', i === index));
    }

    swatches.forEach((swatch, i) => {
      swatch.addEventListener('mouseenter', () => showImg(i));
      swatch.addEventListener('mouseleave', () => showImg(selectedIdx));
      swatch.addEventListener('click', () => {
        selectedIdx = i;
        selectSwatch(i);
      });
    });
  });
}

(() => {
  const track = document.getElementById('carousel-track');
  const counter = document.getElementById('carousel-counter');
  const progressFill = document.getElementById('carousel-progress-fill');
  const prevBtn = document.getElementById('carousel-prev') as HTMLButtonElement | null;
  const nextBtn = document.getElementById('carousel-next') as HTMLButtonElement | null;
  const carouselEl = document.getElementById('bestsellers-carousel');
  if (!track || !counter || !progressFill || !prevBtn || !nextBtn || !carouselEl) return;

  const total = track.children.length;
  if (total === 0) return;
  let idx = 0;

  initColorSwatches();
  initSizesScroll();
  initSizeSelect();

  function pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  function render(): void {
    track!.style.transform = `translateX(-${idx * 100}%)`;
    counter!.textContent = `${pad(idx + 1)} / ${pad(total)}`;
    progressFill!.style.width = `${((idx + 1) / total) * 100}%`;
    prevBtn!.disabled = idx === 0;
    nextBtn!.disabled = idx === total - 1;
  }

  prevBtn.addEventListener('click', () => {
    if (idx > 0) { idx--; render(); }
  });
  nextBtn.addEventListener('click', () => {
    if (idx < total - 1) { idx++; render(); }
  });

  let touchStartX = 0;
  let touchStartTarget: EventTarget | null = null;
  carouselEl.addEventListener('touchstart', (e) => {
    touchStartX = (e as TouchEvent).touches[0]!.clientX;
    touchStartTarget = e.target;
  }, { passive: true });
  carouselEl.addEventListener('touchend', (e) => {
    if ((touchStartTarget as Element | null)?.closest('.product-card__sizes-wrap')) return;
    const delta = touchStartX - (e as TouchEvent).changedTouches[0]!.clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0 && idx < total - 1) idx++;
      else if (delta < 0 && idx > 0) idx--;
      render();
    }
  });

  render();
})();

// ─── NEW ARRIVALS CAROUSEL ───
// Les cartes (.na-card) sont rendues en SSR à partir des vrais produits.

(() => {
  const naTrack = document.getElementById('na-track');
  const naPrev = document.getElementById('na-prev');
  const naNext = document.getElementById('na-next');
  const naCarousel = document.getElementById('na-carousel');
  if (!naTrack || !naPrev || !naNext || !naCarousel) return;

  const naTotal = naTrack.children.length;
  if (naTotal === 0) return;
  let naIdx = 0;

  function getVisible(): number {
    if (window.innerWidth <= 480) return 1;
    if (window.innerWidth <= 1200) return 2;
    return 3;
  }

  function stepWidth(): number {
    const card = naTrack!.querySelector<HTMLElement>('.na-card');
    return card ? card.offsetWidth + 3 : 0;
  }

  function renderNa(): void {
    const maxIdx = Math.max(0, naTotal - getVisible());
    naIdx = Math.max(0, Math.min(naIdx, maxIdx));
    naTrack!.style.transform = `translateX(-${naIdx * stepWidth()}px)`;
    naPrev!.classList.toggle('is-hidden', naIdx === 0);
    naNext!.classList.toggle('is-hidden', naIdx >= maxIdx);
  }

  /** Calque la hauteur de `.new-arrivals__header` sur celle des images des
   * cartes (pas la carte entière, image seule) — pas de relation flex/grid
   * directe possible entre les deux, donc on mesure le rendu réel. Neutralisé
   * en dessous de 768px (layout empilé, cf. media query dans home.css) : on
   * efface l'inline style pour laisser `height: auto` reprendre la main. */
  function syncHeaderHeight(): void {
    const header = document.querySelector<HTMLElement>('.new-arrivals__header');
    const image = naTrack!.querySelector<HTMLElement>('.na-card__image-wrap');
    if (!header || !image) return;

    if (window.innerWidth <= 768) {
      header.style.height = '';
      return;
    }
    header.style.height = `${image.getBoundingClientRect().height}px`;
  }

  naPrev.addEventListener('click', () => {
    if (naIdx > 0) { naIdx--; renderNa(); }
  });
  naNext.addEventListener('click', () => {
    if (naIdx < naTotal - getVisible()) { naIdx++; renderNa(); }
  });

  let naTouchX = 0;
  naCarousel.addEventListener('touchstart', (e) => { naTouchX = (e as TouchEvent).touches[0]!.clientX; }, { passive: true });
  naCarousel.addEventListener('touchend', (e) => {
    const d = naTouchX - (e as TouchEvent).changedTouches[0]!.clientX;
    const maxIdx = naTotal - getVisible();
    if (Math.abs(d) > 50) {
      if (d > 0 && naIdx < maxIdx) naIdx++;
      else if (d < 0 && naIdx > 0) naIdx--;
      renderNa();
    }
  });

  window.addEventListener('resize', () => {
    renderNa();
    syncHeaderHeight();
  });
  renderNa();
  syncHeaderHeight();
})();
